/**
 * Responsibility: Implement task events behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
import type { JobType } from './router'
export type BackgroundTaskType = JobType | 'image'
export interface BackgroundTaskInfo {
  id: string
  type: BackgroundTaskType
  startedAt: number
}

const TASK_EVENT = 'am2:background-task'

function getTaskEventTarget(): Window | null {
  return typeof window !== 'undefined' ? window : null
}

export function emitTaskStart(type: BackgroundTaskType): string {
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const detail = { kind: 'start', task: { id, type, startedAt: Date.now() } }
  const target = getTaskEventTarget()
  if (target) target.dispatchEvent(new CustomEvent(TASK_EVENT, { detail }))
  return id
}

export function emitTaskEnd(id: string, ok: boolean): void {
  const detail = { kind: 'end', id, ok, endedAt: Date.now() }
  const target = getTaskEventTarget()
  if (target) target.dispatchEvent(new CustomEvent(TASK_EVENT, { detail }))
}

export function onBackgroundTaskEvent(
  handler: (event: { kind: 'start'; task: BackgroundTaskInfo } | { kind: 'end'; id: string; ok: boolean; endedAt: number }) => void,
): () => void {
  const listener = (e: Event) => {
    const ce = e as CustomEvent
    if (!ce?.detail || (ce.detail.kind !== 'start' && ce.detail.kind !== 'end')) return
    handler(ce.detail)
  }
  const target = getTaskEventTarget()
  if (!target) return () => {}
  target.addEventListener(TASK_EVENT, listener)
  return () => target.removeEventListener(TASK_EVENT, listener)
}
